import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import type {
  Entity,
  FilterByID,
  MetricsType,
  FiltersOptions,
  SelectedFilters,
  JourneyOverviewDataTable,
  SelectedFilterColumnsData,
  CurrentJourneyAndTouchpointBreadCrumbData,
  JourneyOverviewDataTableQuery,
  SavedDashboardFiltersList,
  SavedDashboardFiltersListQuery,
  SavedDashboardFiltersListData,
  SelectedDashboardEntityFilters,
} from 'src/types/analytics-genesis';
import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import {isEqual, omit} from 'lodash';
import * as reduxApi from 'src/utils/redux-api-v2';
import {thunkify as flow} from 'src/utils/thunks';
import {ANALYTICS_API_V3} from 'src/utils/analytics-api-migration';
import {fetching, key} from 'src/utils/redux';
import {
  journeyTableColumnsQuery,
  touchpointTableColumnsQuery,
  peopleTableColumnsQuery,
  transformEntities,
  getJourneyDashboardName,
} from 'src/utils/analytics-genesis';


export const SET_SELECTED_FILTERS =
  'analytics-journeys-overview/set-selected-filters';
export const SET_SELECTED_ENTITIES =
  'analytics-journeys-overview/set-selected-entities';
export const SET_SELECTED_JOURNEYS =
  'analytics-journeys-overview/set-selected-journeys';
export const SET_SELECTED_DATE_RANGE_AND_FREQUENCY =
  'analytics-journeys-overview/set-selected-date-range';
export const SET_SELECTED_METRICS_TYPE =
  'analytics-journeys-overview/set-selected-metrics-type';
export const SET_CHART_FREQUENCY =
  'analytics-journeys-overview/receive-chart-frequency';
export const RECEIVE_FILTERS_OPTIONS =
  'analytics-journeys-overview/receive-filters-options';
export const RECEIVE_SENT_FROM_OPTIONS =
  'analytics-journeys-overview/receive-sent-from-options';
export const RECEIVE_JOURNEY_OPTIONS =
  'analytics-journeys-overview/receive-journey-options';
export const RECEIVE_JOURNEY_TABLE_COLUMNS =
  'analytics-journeys-overview/receive-journey-table-columns';
export const RECEIVE_TOUCHPOINT_TABLE_COLUMNS =
  'analytics-journeys-overview/receive-touchpoint-table-columns';
export const RECEIVE_PEOPLE_TABLE_COLUMNS =
  'analytics-journeys-overview/receive-people-table-columns';
export const SET_CURRENT_JOURNEY_AND_TOUCHPOINT_DATA =
  'analytics-journeys-overview/set-current-journey-and-touchpoint-data';
export const RECEIVE_SAVED_DASHBOARDS =
  'analytics-journeys-overview/receive-saved-dashboards';
export const SET_CURRENT_DASHBOARD =
  'analytics-journeys-overview/set-current-dashboard';
export const CLEAR_SELECTED_FILTERS =
  'analytics-journeys-overview/clear-filters';
export const SET_LOADING_DASHBOARD_FILTERS =
  'analytics-journeys-overview/loading-dashboard-filters';
export const SET_SELECTED_DASHBOARD_ENTITY_FILTERS =
  'analytics-journeys-overview/set-selected-dashboard-entity-filters';
export const SET_NAVIGATION_DOWN =
  'analytics-journeys-overview/set-navigation-down';
export const SET_NAVIGATION_UP =
  'analytics-journeys-overview/set-navigation-up';
export const SET_ALL_JOURNEYS_ENTITIES =
  'analytics-journeys-overview/set-all-journeys-entities';
export const SET_ALL_TOUCHPOINTS_ENTITIES =
  'analytics-journeys-overview/set-all-touchpoints-entities';
export const SET_ALL_PEOPLE_ENTITIES =
  'analytics-journeys-overview/set-all-people-entities';
export const SET_ALL_JOURNEYS_FILTERS =
  'analytics-journeys-overview/set-all-journeys-filters';
export const SET_ALL_PEOPLE_FILTERS =
  'analytics-journeys-overview/set-all-people-filters';
export const SET_ALL_TOUCHPOINTS_FILTERS =
  'analytics-journeys-overview/set-all-touchpoints-filters';
export const SET_SELECTED_DATES =
  'analytics-journeys-overview/set-selected-dates';
export const SET_ENTITIES = 'analytics-journeys-overview/set-entities';
export const SET_FILTERS = 'analytics-journeys-overview/set-filters';

type SetSelectedFiltersAction = {
  type: typeof SET_SELECTED_FILTERS;
  payload: SelectedFilters;
};

type SetSelectedEntitiesAction = {
  type: typeof SET_SELECTED_ENTITIES;
  payload: Array<Entity>;
};

type SetSelectedJourneysAction = {
  type: typeof SET_SELECTED_JOURNEYS;
  payload: {
    dashboardKey: string;
    journeys: Array<FilterByID>;
  };
};

type SetSelectedDateRangeAndFrequencyAction = {
  type: typeof SET_SELECTED_DATE_RANGE_AND_FREQUENCY;
  payload: DateRangeWithTimezone & {
    chartFrequency: string;
    dashboardKey: string;
  };
};

type SetSelectedMetricsTypeAction = {
  type: typeof SET_SELECTED_METRICS_TYPE;
  payload: MetricsType;
};

type SetCurrentJourneyAndTouchpointDataAction = {
  type: typeof SET_CURRENT_JOURNEY_AND_TOUCHPOINT_DATA;
  payload: CurrentJourneyAndTouchpointBreadCrumbData;
};

type SetChartFrequencyAction = {
  type: typeof SET_CHART_FREQUENCY;
  payload: string;
};

type ReceiveFiltersOptionsAction = {
  type: typeof RECEIVE_FILTERS_OPTIONS;
  payload: FiltersOptions;
};

type ReceiveSentFromOptionsAction = {
  type: typeof RECEIVE_SENT_FROM_OPTIONS;
  payload: Array<string>;
};

type ReceiveJourneyOptionsAction = {
  type: typeof RECEIVE_JOURNEY_OPTIONS;
  payload: Array<FilterByID>;
};

type ReceiveJourneyTableColumnsAction = {
  type: typeof RECEIVE_JOURNEY_TABLE_COLUMNS;
  payload: SelectedFilterColumnsData;
};

type ReceiveTouchpointTableColumnsAction = {
  type: typeof RECEIVE_TOUCHPOINT_TABLE_COLUMNS;
  payload: SelectedFilterColumnsData;
};

type ReceivePeopleTableColumnsAction = {
  type: typeof RECEIVE_PEOPLE_TABLE_COLUMNS;
  payload: SelectedFilterColumnsData;
};

type ReceiveSavedDashboardsAction = {
  type: typeof RECEIVE_SAVED_DASHBOARDS;
  payload: {
    filters: SavedDashboardFiltersListData;
    dashboard: string;
    product: string;
  };
};

type SetCurrentDashboardAction = {
  type: typeof SET_CURRENT_DASHBOARD;
  payload: {
    currentDashboard: SavedDashboardFiltersList | null;
    dashboardKey: string;
  };
};

type ClearSelectedFiltersAction = {
  type: typeof CLEAR_SELECTED_FILTERS;
  payload: {
    dashboardKey: string;
  };
};

type SetLoadingDashboardFiltersAction = {
  type: typeof SET_LOADING_DASHBOARD_FILTERS;
  payload: boolean;
};

type SetSelectedDashboardEntityFiltersAction = {
  type: typeof SET_SELECTED_DASHBOARD_ENTITY_FILTERS;
  payload: {
    dashboardKey: string;
  } & SelectedDashboardEntityFilters;
};

type SetNavigationDownAction = {
  type: typeof SET_NAVIGATION_DOWN;
  payload: boolean;
};

type SetNavigationUpAction = {
  type: typeof SET_NAVIGATION_UP;
  payload: boolean;
};

type SetAllJourneysEntitiesAction = {
  type: typeof SET_ALL_JOURNEYS_ENTITIES;
  payload: {
    dashboardKey: string;
    entities: Array<Entity>;
  };
};

type SetAllTouchpointsEntitiesAction = {
  type: typeof SET_ALL_TOUCHPOINTS_ENTITIES;
  payload: {
    dashboardKey: string;
    entities: Array<Entity>;
  };
};

type SetAllPeopleEntitiesAction = {
  type: typeof SET_ALL_PEOPLE_ENTITIES;
  payload: {
    dashboardKey: string;
    entities: Array<Entity>;
  };
};

type SetAllJourneysFiltersAction = {
  type: typeof SET_ALL_JOURNEYS_FILTERS;
  payload: {
    dashboardKey: string;
    filters: SelectedFilters;
  };
};

type SetAllPeopleFiltersAction = {
  type: typeof SET_ALL_PEOPLE_FILTERS;
  payload: {
    dashboardKey: string;
    filters: SelectedFilters;
  };
};

type SetAllTouchpointsFiltersAction = {
  type: typeof SET_ALL_TOUCHPOINTS_FILTERS;
  payload: {
    dashboardKey: string;
    filters: SelectedFilters;
  };
};

type SetSelectedDatesAction = {
  type: typeof SET_SELECTED_DATES;
  payload: {
    dashboardKey: string;
    dates: DateRangeWithTimezone;
  };
};

type SetEntitiesAction = {
  type: typeof SET_ENTITIES;
  payload: {
    dashboardKey: string;
    entities: Array<Entity>;
  };
};

type SetFiltersAction = {
  type: typeof SET_FILTERS;
  payload: {
    dashboardKey: string;
    filters: SelectedFilters;
  };
};

export type AnalyticsJourneysOverviewActions =
  | SetSelectedFiltersAction
  | SetSelectedEntitiesAction
  | SetSelectedJourneysAction
  | SetSelectedDateRangeAndFrequencyAction
  | SetSelectedMetricsTypeAction
  | SetChartFrequencyAction
  | ReceiveFiltersOptionsAction
  | ReceiveSentFromOptionsAction
  | ReceiveJourneyOptionsAction
  | ReceiveJourneyTableColumnsAction
  | ReceiveTouchpointTableColumnsAction
  | ReceivePeopleTableColumnsAction
  | SetCurrentJourneyAndTouchpointDataAction
  | ReceiveSavedDashboardsAction
  | SetCurrentDashboardAction
  | ClearSelectedFiltersAction
  | SetLoadingDashboardFiltersAction
  | SetSelectedDashboardEntityFiltersAction
  | SetNavigationDownAction
  | SetNavigationUpAction
  | SetAllJourneysEntitiesAction
  | SetAllTouchpointsEntitiesAction
  | SetAllPeopleEntitiesAction
  | SetAllJourneysFiltersAction
  | SetAllPeopleFiltersAction
  | SetAllTouchpointsFiltersAction
  | SetSelectedDatesAction
  | SetEntitiesAction
  | SetFiltersAction;

export const setSelectedFilters = (
  payload: SelectedFilters,
): SetSelectedFiltersAction => ({
  type: SET_SELECTED_FILTERS,
  payload,
});

export const setSelectedEntities = (
  payload: Array<Entity>,
): SetSelectedEntitiesAction => ({
  type: SET_SELECTED_ENTITIES,
  payload,
});

export const setSelectedJourneys = (
  dashboardKey: string,
  journeys: Array<FilterByID>,
): SetSelectedJourneysAction => ({
  type: SET_SELECTED_JOURNEYS,
  payload: {dashboardKey, journeys},
});

export const setSelectedDateRangeAndFrequency = (
  payload: DateRangeWithTimezone & {
    chartFrequency: string;
    dashboardKey: string;
  },
): SetSelectedDateRangeAndFrequencyAction => ({
  type: SET_SELECTED_DATE_RANGE_AND_FREQUENCY,
  payload,
});

export const setSelectedMetricsType = (
  payload: MetricsType,
): SetSelectedMetricsTypeAction => ({
  type: SET_SELECTED_METRICS_TYPE,
  payload,
});

export const setChartFrequency = (
  payload: string,
): SetChartFrequencyAction => ({
  type: SET_CHART_FREQUENCY,
  payload,
});

const receiveFiltersOptions = (
  payload: FiltersOptions,
): ReceiveFiltersOptionsAction => ({
  type: RECEIVE_FILTERS_OPTIONS,
  payload,
});

const receiveSentFromOptions = (
  payload: Array<string>,
): ReceiveSentFromOptionsAction => ({
  type: RECEIVE_SENT_FROM_OPTIONS,
  payload,
});

const receiveJourneyOptions = (
  payload: Array<FilterByID>,
): ReceiveJourneyOptionsAction => ({
  type: RECEIVE_JOURNEY_OPTIONS,
  payload,
});

const receiveJourneyTableColumnSelection = (
  payload: SelectedFilterColumnsData,
): ReceiveJourneyTableColumnsAction => ({
  type: RECEIVE_JOURNEY_TABLE_COLUMNS,
  payload,
});

const receiveTouchpointTableColumnSelection = (
  payload: SelectedFilterColumnsData,
): ReceiveTouchpointTableColumnsAction => ({
  type: RECEIVE_TOUCHPOINT_TABLE_COLUMNS,
  payload,
});

const receivePeopleTableColumnSelection = (
  payload: SelectedFilterColumnsData,
): ReceivePeopleTableColumnsAction => ({
  type: RECEIVE_PEOPLE_TABLE_COLUMNS,
  payload,
});

export const setCurrentJourneyAndTouchpointData = (
  payload: CurrentJourneyAndTouchpointBreadCrumbData,
): SetCurrentJourneyAndTouchpointDataAction => ({
  type: SET_CURRENT_JOURNEY_AND_TOUCHPOINT_DATA,
  payload,
});

const receiveSavedDashboardFilterListData = (payload: {
  filters: SavedDashboardFiltersListData;
  dashboard: string;
  product: string;
}): ReceiveSavedDashboardsAction => ({
  type: RECEIVE_SAVED_DASHBOARDS,
  payload,
});

export const setCurrentDashboard = (payload: {
  currentDashboard: SavedDashboardFiltersList | null;
  dashboardKey: string;
}): SetCurrentDashboardAction => ({
  type: SET_CURRENT_DASHBOARD,
  payload,
});

export const clearSelectedFilters = (
  dashboardKey: string,
): ClearSelectedFiltersAction => ({
  type: CLEAR_SELECTED_FILTERS,
  payload: {dashboardKey},
});

export const setLoadingDashboardFilters = (
  payload: boolean,
): SetLoadingDashboardFiltersAction => ({
  type: SET_LOADING_DASHBOARD_FILTERS,
  payload,
});

export const setSelectedDashboardEntityFilters = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetSelectedDashboardEntityFiltersAction => ({
  type: SET_SELECTED_DASHBOARD_ENTITY_FILTERS,
  payload: {dashboardKey, entities},
});

export const setNavigationDown = (
  isNavigationDown: boolean,
): SetNavigationDownAction => ({
  type: SET_NAVIGATION_DOWN,
  payload: isNavigationDown,
});

export const setNavigationUp = (
  isNavigationUp: boolean,
): SetNavigationUpAction => ({
  type: SET_NAVIGATION_UP,
  payload: isNavigationUp,
});

export const setAllJourneysEntities = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetAllJourneysEntitiesAction => ({
  type: SET_ALL_JOURNEYS_ENTITIES,
  payload: {dashboardKey, entities},
});

export const setAllTouchpointsEntities = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetAllTouchpointsEntitiesAction => ({
  type: SET_ALL_TOUCHPOINTS_ENTITIES,
  payload: {dashboardKey, entities},
});

export const setAllPeopleEntities = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetAllPeopleEntitiesAction => ({
  type: SET_ALL_PEOPLE_ENTITIES,
  payload: {dashboardKey, entities},
});

export const setAllJourneysFilters = (
  dashboardKey: string,
  filters: SelectedFilters,
): SetAllJourneysFiltersAction => ({
  type: SET_ALL_JOURNEYS_FILTERS,
  payload: {dashboardKey, filters},
});

export const setAllPeopleFilters = (
  dashboardKey: string,
  filters: SelectedFilters,
): SetAllPeopleFiltersAction => ({
  type: SET_ALL_PEOPLE_FILTERS,
  payload: {dashboardKey, filters},
});

export const setAllTouchpointsFilters = (
  dashboardKey: string,
  filters: SelectedFilters,
): SetAllTouchpointsFiltersAction => ({
  type: SET_ALL_TOUCHPOINTS_FILTERS,
  payload: {dashboardKey, filters},
});

export const setSelectedDates = (
  dashboardKey: string,
  dates: DateRangeWithTimezone,
): SetSelectedDatesAction => ({
  type: SET_SELECTED_DATES,
  payload: {dashboardKey, dates},
});

export const setEntities = (
  dashboardKey: string,
  entities: Array<Entity>,
): SetEntitiesAction => ({
  type: SET_ENTITIES,
  payload: {dashboardKey, entities},
});

export const setFilters = (
  dashboardKey: string,
  filters: SelectedFilters,
): SetFiltersAction => ({
  type: SET_FILTERS,
  payload: {dashboardKey, filters},
});

const analyticsOptions: ApiOptions = {apiPath: ANALYTICS_API_V3};
const analyticsApiOptions: ApiPostOptions = {apiPath: ANALYTICS_API_V3};

export const getFiltersOptions: () => ThunkAction<unknown> = flow(
  key(() => `filtersOptions`),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  const allFilters = {
    filter_list: [
      'journey_lifecycle_goal',
      'journey_business_goal',
      'journey_category',
      'journey_status',
      'recipient_type',
      'journey_name',
      'event_name',
      'event_type',
    ],
  } as const;
  try {
    const response = await dispatch(
      reduxApi.post('/filters', allFilters, {}, analyticsApiOptions),
    );
    dispatch(receiveFiltersOptions(response));
  } catch (error: any) {
    dispatch(
      receiveFiltersOptions({
        journey_lifecycle_goal: [],
        journey_business_goal: [],
        journey_category: [],
        journey_status: [],
        recipient_type: [],
        journey_name: [],
        event_name: [],
        event_type: [],
      }),
    );
    console.error('Error Fetching Filters Options:', error);
  }
});

export const getJourneyOptions: (
  query: string,
  limit: number,
) => ThunkAction<unknown> = flow(
  key((query, limit) => `journeyOptions-${query}-${limit}`),
  fetching(),
)((query, limit) => async (dispatch: Dispatch) => {
  const journeyFilter = {filter_list: ['journey_name'], query, limit} as const;
  try {
    const {journey_name} = await dispatch(
      reduxApi.post('/filters', journeyFilter, {}, analyticsApiOptions),
    );
    dispatch(receiveJourneyOptions(journey_name));
  } catch (error: any) {
    console.error('Error Fetching Journey Options:', error);
    dispatch(receiveJourneyOptions([]));
  }
});

export const getSentFromOptions: (searchQuery: string) => ThunkAction<unknown> =
  flow(
    key((searchQuery) => `sentFromOptions-${searchQuery}`),
    fetching(),
  )((searchQuery) => async (dispatch: Dispatch) => {
    try {
      const {attribute_values} = await dispatch(
        reduxApi.get('typeahead/event_resources/sent_event/from_address', {
          search_string: searchQuery,
        }),
      );
      dispatch(receiveSentFromOptions(attribute_values));
    } catch (error: any) {
      console.error('Error Fetching Sent From Options:', error);
      dispatch(receiveSentFromOptions([]));
    }
  });

export const getJourneyTableFilterColumns: () => ThunkAction<unknown> = flow(
  key(() => 'getJourneyTableFilterColumns'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: SelectedFilterColumnsData = await dispatch(
      reduxApi.get(
        '/journeys-overview/column-selection',
        journeyTableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receiveJourneyTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching journey column filter:', error);
  }
});

export const getTouchpointTableFilterColumns: () => ThunkAction<unknown> = flow(
  key(() => 'getTouchpointTableFilterColumns'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: SelectedFilterColumnsData = await dispatch(
      reduxApi.get(
        '/journeys-overview/column-selection',
        touchpointTableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receiveTouchpointTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching touchpoint overview column filter:', error);
  }
});

export const getPeopleTableFilterColumns: () => ThunkAction<unknown> = flow(
  key(() => 'getPeopleTableFilterColumns'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: SelectedFilterColumnsData = await dispatch(
      reduxApi.get(
        '/journeys-overview/column-selection',
        peopleTableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receivePeopleTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching people column filter:', error);
  }
});

export const saveSelectedFilterColumns: (
  columnData: SelectedFilterColumnsData,
) => ThunkAction<unknown> = flow(
  key(
    (columnData) => `saveSelectedFiltersColumns-${JSON.stringify(columnData)}`,
  ),
  fetching(),
)(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/journeys-overview/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);

export const exportJourneyOverviewTableData: (
  query: JourneyOverviewDataTableQuery,
  url: string,
) => ThunkAction<unknown> = flow(
  key((query, url) => `journeyDataTable-${JSON.stringify(query)}-${url}`),
  fetching(),
)((query, url) => async (dispatch: Dispatch) => {
  try {
    await dispatch(reduxApi.post(url, query, {}));
  } catch (error: any) {
    console.error('Error exporting journey overview data:', error);
  }
});

export const getJourneyWorkflowDetailsColumns: (
  journeyId: string,
  touchpointId?: string,
) => ThunkAction<unknown> = flow(
  key((journeyId) => `getJourneyWorkflowDetails-${journeyId}`),
  fetching(),
)(
  (journeyId, touchpointId) =>
    async (dispatch: Dispatch, getState: GetState) => {
      try {
        const currentJourneyAndTouchpoint: CurrentJourneyAndTouchpointBreadCrumbData =
          {
            journey: {},
            touchpoint: {},
          };
        if (journeyId) {
          const response = await dispatch(
            reduxApi.get(`workflows/${journeyId}`),
          );
          currentJourneyAndTouchpoint.journey = {
            id: journeyId,
            name: response?.name,
          };

          if (touchpointId) {
            const touchpoint = response?.events_summary?.find(
              (tp: any) => tp.id === touchpointId,
            );

            if (touchpoint) {
              const {title, send_type} = touchpoint;

              currentJourneyAndTouchpoint.touchpoint = {
                id: touchpointId,
                name: title,
                sendType: send_type,
              };
            }
          } else {
            currentJourneyAndTouchpoint.touchpoint = {};
          }
        }
        dispatch(
          setCurrentJourneyAndTouchpointData(currentJourneyAndTouchpoint),
        );
      } catch (error: any) {
        console.error('Error saving overview column filter:', error);
      }
    },
);

export const getSavedAllJourneyDashboards: (
  query: SavedDashboardFiltersListQuery,
) => ThunkAction<unknown> = flow(
  key(() => 'j1-analytics-deliverability-engagement-saved-dashboards'),
  fetching(),
)((query) => async (dispatch: Dispatch) => {
  try {
    dispatch(setLoadingDashboardFilters(true));
    const {
      filters,
    }: {
      filters: SavedDashboardFiltersListData;
    } = await dispatch(
      reduxApi.get('/journeys-overview/save-filter', query, analyticsOptions),
    );
    dispatch(receiveSavedDashboardFilterListData({filters, ...query}));
    const loadSavedFilters = () => {
      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {filters = {}} = defaultDashboard;
        const {
          entity_data_filter = [{id: new Date().getTime()}],
          event_types = [],
          journey_ids = [],
          from_address = [],
          journey_status = [],
          recipient_type = [],
          journey_categories = [],
          journey_business_goals = [],
          journey_lifecycle_goals = [],
          selected_date_range = {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
        } = filters ?? {};
        dispatch(setEntities('journeys_all', entity_data_filter));
        dispatch(
          setFilters('journeys_all', {
            event_types,
            journey_ids: journey_ids.map(({id}) => id),
            from_address,
            journey_status,
            recipient_type,
            journey_categories,
            journey_business_goals,
            journey_lifecycle_goals,
          }),
        );
        dispatch(setSelectedDates('journeys_all', selected_date_range));
      }
      dispatch(
        setCurrentDashboard({
          currentDashboard: defaultDashboard ?? null,
          dashboardKey: 'journeys_all',
        }),
      );
    };
    loadSavedFilters();
  } catch (error: any) {
    console.error('Error fetching saved dashboard filter:', error);
  } finally {
    dispatch(setLoadingDashboardFilters(false));
    dispatch(setNavigationDown(false));
    dispatch(setNavigationUp(false));
  }
});

export const getSavedAllTouchpointDashboards: (
  query: SavedDashboardFiltersListQuery,
  journeyId: string,
) => ThunkAction<unknown> = flow(
  key(() => 'j1-analytics-deliverability-engagement-saved-dashboards'),
  fetching(),
)((query, journeyId) => async (dispatch: Dispatch, getState: GetState) => {
  try {
    dispatch(setLoadingDashboardFilters(true));
    const {
      filters,
    }: {
      filters: SavedDashboardFiltersListData;
    } = await dispatch(
      reduxApi.get('/journeys-overview/save-filter', query, analyticsOptions),
    );
    dispatch(receiveSavedDashboardFilterListData({filters, ...query}));
    const dashboardName = getJourneyDashboardName(journeyId, undefined);
    const isNavigationDown =
      getState().analyticsJourneysOverview.isNavigationDown;
    const isNavigationUp = getState().analyticsJourneysOverview.isNavigationUp;

    const parentEntitiesFilters = getState().analyticsJourneysOverview
      .selectedEntities['journeys_all'] ?? [{id: new Date().getTime()}];

    const parentJourneyFilters =
      getState().analyticsJourneysOverview.selectedFilters['journeys_all'] ??
      {};

    const parentDateRange = getState().analyticsJourneysOverview.selectedDates[
      'journeys_all'
    ] ?? {
      startDateUTC: null,
      startDate: null,
      endDateUTC: null,
      endDate: null,
    };

    const savedDashboardFilters =
      getState().analyticsJourneysOverview.saveDashboardFilterList[
        'journeys_all'
      ] ?? [];

    const parentDefaultDashboard = savedDashboardFilters?.find(
      ({is_default}) => is_default,
    );

    const {filters: parentFilters = {}} = parentDefaultDashboard ?? {};

    const {
      entity_data_filter: entityFilters = [{id: new Date().getTime()}],
      selected_date_range = {
        startDateUTC: null,
        startDate: null,
        endDateUTC: null,
        endDate: null,
      },
      ...journeyFilters
    } = parentFilters ?? {};

    const loadSavedFilters = () => {
      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {filters} = defaultDashboard;
        const {
          entity_data_filter = [{id: new Date().getTime()}],
          event_types = [],
          journey_ids = [],
          from_address = [],
          journey_status = [],
          recipient_type = [],
          journey_categories = [],
          journey_business_goals = [],
          journey_lifecycle_goals = [],
          selected_date_range = {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
        } = filters ?? {};
        dispatch(setEntities(dashboardName, entity_data_filter));
        dispatch(
          setFilters(dashboardName, {
            event_types,
            journey_ids: journey_ids.map(({id}) => id),
            from_address,
            journey_status,
            recipient_type,
            journey_categories,
            journey_business_goals,
            journey_lifecycle_goals,
          }),
        );
        dispatch(setSelectedDates(dashboardName, selected_date_range));
      }
      dispatch(
        setCurrentDashboard({
          currentDashboard: defaultDashboard ?? null,
          dashboardKey: dashboardName,
        }),
      );
    };

    if (isNavigationDown === null || isNavigationUp) {
      loadSavedFilters();
      return;
    }

    if (isNavigationDown) {
      const isEntityFiltersChanged = !isEqual(
        transformEntities(entityFilters),
        transformEntities(parentEntitiesFilters),
      );

      const isJourneyFiltersChanged = !isEqual(
        journeyFilters,
        parentJourneyFilters,
      );

      const isDateRangeChanged = !isEqual(parentDateRange, selected_date_range);

      if (
        isEntityFiltersChanged ||
        isJourneyFiltersChanged ||
        isDateRangeChanged
      ) {
        dispatch(setEntities(dashboardName, parentEntitiesFilters));
        dispatch(setFilters(dashboardName, parentJourneyFilters));
        dispatch(setSelectedDates(dashboardName, parentDateRange));
      } else {
        loadSavedFilters();
      }
      return;
    }
  } catch (error: any) {
    console.error('Error fetching saved dashboard filter:', error);
  } finally {
    dispatch(setLoadingDashboardFilters(false));
    dispatch(setNavigationDown(false));
    dispatch(setNavigationUp(false));
  }
});

export const getSavedAllPeopleDashboards: (
  query: SavedDashboardFiltersListQuery,
  journeyId: string,
  touchpointId?: string,
) => ThunkAction<unknown> = flow(
  key(() => 'j1-analytics-deliverability-engagement-saved-dashboards'),
  fetching(),
)(
  (query, journeyId, touchpointId) =>
    async (dispatch: Dispatch, getState: GetState) => {
      try {
        dispatch(setLoadingDashboardFilters(true));
        const {
          filters,
        }: {
          filters: SavedDashboardFiltersListData;
        } = await dispatch(
          reduxApi.get(
            '/journeys-overview/save-filter',
            query,
            analyticsOptions,
          ),
        );
        dispatch(receiveSavedDashboardFilterListData({filters, ...query}));
        const dashboardName = getJourneyDashboardName(journeyId, touchpointId);
        const isNavigationDown =
          getState().analyticsJourneysOverview.isNavigationDown;
        const isNavigationUp =
          getState().analyticsJourneysOverview.isNavigationUp;

        const parentEntitiesFilters = getState().analyticsJourneysOverview
          .selectedEntities[`journeys_${journeyId}`] ?? [
          {id: new Date().getTime()},
        ];

        const parentJourneyFilters =
          getState().analyticsJourneysOverview.selectedFilters[
            `journeys_${journeyId}`
          ] ?? {};

        const parentDateRange = getState().analyticsJourneysOverview
          .selectedDates[`journeys_${journeyId}`] ?? {
          startDateUTC: null,
          startDate: null,
          endDateUTC: null,
          endDate: null,
        };

        const savedDashboardFilters =
          getState().analyticsJourneysOverview.saveDashboardFilterList[
            `journeys_${journeyId}`
          ] ?? [];

        const parentDefaultDashboard = savedDashboardFilters?.find(
          ({is_default}) => is_default,
        );

        const {filters: parentFilters = {}} = parentDefaultDashboard ?? {};
        const {
          entity_data_filter: entityFilters = [{id: new Date().getTime()}],
          selected_date_range = {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
          ...journeyFilters
        } = parentFilters ?? {};

        const loadSavedFilters = () => {
          const defaultDashboard = filters.find(({is_default}) => is_default);
          if (defaultDashboard) {
            const {filters} = defaultDashboard;
            const {
              entity_data_filter = [{id: new Date().getTime()}],
              event_types = [],
              journey_ids = [],
              from_address = [],
              journey_status = [],
              recipient_type = [],
              journey_categories = [],
              journey_business_goals = [],
              journey_lifecycle_goals = [],
              selected_date_range = {
                startDateUTC: null,
                startDate: null,
                endDateUTC: null,
                endDate: null,
              },
            } = filters ?? {};
            dispatch(setEntities(dashboardName, entity_data_filter));
            dispatch(
              setFilters(dashboardName, {
                event_types,
                journey_ids: journey_ids.map(({id}) => id),
                from_address,
                journey_status,
                recipient_type,
                journey_categories,
                journey_business_goals,
                journey_lifecycle_goals,
              }),
            );
            dispatch(setSelectedDates(dashboardName, selected_date_range));
          }
          dispatch(
            setCurrentDashboard({
              currentDashboard: defaultDashboard ?? null,
              dashboardKey: dashboardName,
            }),
          );
        };

        if (isNavigationDown === null || isNavigationUp) {
          loadSavedFilters();
          return;
        }

        if (isNavigationDown) {
          const isEntityFiltersChanged = !isEqual(
            transformEntities(entityFilters),
            transformEntities(parentEntitiesFilters),
          );

          const isJourneyFiltersChanged = !isEqual(
            journeyFilters,
            parentJourneyFilters,
          );

          const isDateRangeChanged = !isEqual(
            parentDateRange,
            selected_date_range,
          );

          if (
            isEntityFiltersChanged ||
            isJourneyFiltersChanged ||
            isDateRangeChanged
          ) {
            dispatch(setEntities(dashboardName, parentEntitiesFilters));
            dispatch(setFilters(dashboardName, parentJourneyFilters));
            dispatch(setSelectedDates(dashboardName, parentDateRange));
          } else {
            loadSavedFilters();
          }
          return;
        }
      } catch (error: any) {
        console.error('Error fetching saved dashboard filter:', error);
      } finally {
        dispatch(setLoadingDashboardFilters(false));
        dispatch(setNavigationDown(false));
        dispatch(setNavigationUp(false));
      }
    },
);

export const saveJourneyDashboard: (
  dashboardData: SavedDashboardFiltersList,
  dashboardKey: string,
) => ThunkAction<unknown> = flow(
  key(
    (dashboardData) => `saveJourneyDashboard-${JSON.stringify(dashboardData)}`,
  ),
  fetching(),
)(
  (dashboardData, dashboardKey) =>
    async (dispatch: Dispatch, getState: GetState) => {
      try {
        const {
          filters,
        }: {
          filters: SavedDashboardFiltersListData;
        } = await dispatch(
          reduxApi.post(
            '/journeys-overview/save-filter',
            dashboardData,
            {},
            analyticsApiOptions,
          ),
        );
        const {name: dashboardName, product, dashboard, id} = dashboardData;
        dispatch(
          receiveSavedDashboardFilterListData({filters, product, dashboard}),
        );
        const {id: currentDashboardID} =
          getState().analyticsJourneysOverview.currentDashboard[dashboardKey] ??
          {};
        if (!(Boolean(id) && currentDashboardID !== id)) {
          const newDashboard = filters.find(({name}) => name === dashboardName);
          dispatch(
            setCurrentDashboard({
              currentDashboard: newDashboard ?? null,
              dashboardKey,
            }),
          );
        }
      } catch (error: any) {
        console.error('Error fetching saved dashboard filter:', error);
        throw error;
      }
    },
);
